import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Collapse, Descriptions, Form, Input, InputNumber, Modal, Select, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../../shared/components/PageHeader';
import { formatMoney } from '../../../shared/utils/money';
import { cartService } from '../../services/cartService';
import { accessApi } from './api';

const { Text, Paragraph } = Typography;

const STAGE_TAG = {
    grace: ['warning', 'Expired'],
    suspended: ['error', 'Suspended'],
    disabled: ['default', 'Disabled'],
};

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', viewer: 'Viewer' };

/** Owner: invite other Yeap accounts as admin or viewer. */
const Members = ({ teamId }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('admin');
    const load = useCallback(() => accessApi.members(teamId).then((d) => setRows(d.items)).catch(() => setRows([])), [teamId]);
    useEffect(() => { load(); }, [load]);
    const act = (p) => p.then(load).catch((e) => message.error(e.error || t('Failed to save')));
    const roles = [{ value: 'admin', label: t('Admin') }, { value: 'viewer', label: t('Viewer') }];
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space.Compact style={{ maxWidth: 520, width: '100%' }}>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('Email of a Yeap account')} />
                <Select value={role} onChange={setRole} options={roles} style={{ width: 120 }} />
                <Button type="primary" disabled={!email.trim()} onClick={() => act(accessApi.invite(teamId, email.trim(), role).then(() => setEmail('')))}>{t('Invite')}</Button>
            </Space.Compact>
            <Table size="small" rowKey="id" loading={rows === null} dataSource={rows || []} pagination={false}
                columns={[
                    { title: t('Email'), dataIndex: 'email' },
                    { title: t('Role'), render: (_, r) => <Select size="small" value={r.role} options={roles} onChange={(v) => act(accessApi.setRole(teamId, r.id, v))} style={{ width: 110 }} /> },
                    { title: t('Status'), render: (_, r) => (r.status === 'active' ? <Tag color="green">{t('Active')}</Tag> : <Tag>{t('Invited')}</Tag>) },
                    { title: '', render: (_, r) => <Button size="small" danger type="link" onClick={() => act(accessApi.removeMember(teamId, r.id))}>{t('Remove')}</Button> },
                ]} />
        </Space>
    );
};

const Invitations = ({ items, onDone }) => {
    const { t } = useTranslation();
    if (!items?.length) return null;
    const act = (p) => p.then(onDone).catch((e) => message.error(e.error || t('Failed to save')));
    return (
        <>
            {items.map((i) => (
                <Alert key={i.id} type="info" showIcon style={{ marginBottom: 12 }}
                    message={t('{{who}} invited you to team {{team}} as {{role}}', { who: i.invited_by || '', team: i.team, role: t(ROLE_LABEL[i.role] || i.role) })}
                    action={<Space><Button size="small" type="primary" onClick={() => act(accessApi.acceptInvite(i.id))}>{t('Accept')}</Button>
                        <Button size="small" onClick={() => act(accessApi.declineInvite(i.id))}>{t('Decline')}</Button></Space>} />
            ))}
        </>
    );
};

/** Free tier → paid plan through the cart (quantity = seats beyond the free ones). */
const UpgradeModal = ({ open, product, plans, info, team, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const paid = (plans || []).filter((p) => !p.free);
    const [planId, setPlanId] = useState(null);
    const [cycle, setCycle] = useState('monthly');
    const [seats, setSeats] = useState(0);
    const [busy, setBusy] = useState(false);
    const plan = paid.find((p) => p.product_id === planId) || paid[0];
    const free = plan?.free_seats ?? info?.free_seats ?? 50;
    const price = plan?.prices?.find((x) => x.billing_cycle === cycle) || plan?.prices?.[0];

    useEffect(() => {
        if (open) {
            setPlanId(paid[0]?.product_id ?? null);
            setSeats(Math.max(free + 1, info?.users || 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const extra = Math.max(1, seats - free);
    const submit = async () => {
        setBusy(true);
        try {
            const r = await cartService.addItem({
                product_id: plan.product_id, billing_cycle: price.billing_cycle, quantity: extra,
                config: team ? {} : undefined,
            });
            if (r?.success === false) throw r;
            navigate(userPath('/billing/cart'));
        } catch (e) {
            message.error(e?.error || e?.message || t('Failed to add to cart'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open={open} title={t('Upgrade seats')} onCancel={onClose} onOk={submit} confirmLoading={busy}
            okText={t('Add to cart')} okButtonProps={{ disabled: !plan || !price }} destroyOnHidden>
            <Form layout="vertical" requiredMark={false}>
                {paid.length > 1 && (
                    <Form.Item label={t('Plan')}>
                        <Select value={plan?.product_id} onChange={setPlanId} options={paid.map((p) => ({ value: p.product_id, label: p.name }))} />
                    </Form.Item>
                )}
                <Form.Item label={t('Users')} extra={t('{{n}} users are free', { n: free })}>
                    <InputNumber min={free + 1} max={free + (price?.max_quantity > 0 ? price.max_quantity : 10000)} value={seats} onChange={(v) => setSeats(v || 0)} style={{ width: 160 }} />
                </Form.Item>
                {plan?.prices?.length > 1 && (
                    <Form.Item label={t('Billing cycle')}>
                        <Select value={price?.billing_cycle} onChange={setCycle}
                            options={plan.prices.map((p) => ({ value: p.billing_cycle, label: t(p.billing_cycle === 'annually' ? 'Yearly' : 'Monthly') }))} />
                    </Form.Item>
                )}
                {price && (
                    <Text>
                        {t('{{n}} × {{price}}', { n: extra, price: formatMoney(price.price, price.currency) })} = <b>{formatMoney(price.price * extra, price.currency)}</b>
                    </Text>
                )}
            </Form>
        </Modal>
    );
};

/** Paid plan: change total seats (more = prorated invoice, fewer = at once). */
const SeatsModal = ({ open, product, info, onClose, onDone }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [seats, setSeats] = useState(info?.seats || 0);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open) setSeats(info?.seats || 0);
    }, [open, info]);
    useEffect(() => {
        if (!open || !seats) return;
        const id = setTimeout(() => accessApi.seatQuote(product, seats).then(setQuote).catch(() => setQuote(null)), 300);
        return () => clearTimeout(id);
    }, [open, product, seats]);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await accessApi.seats(product, seats);
            if (r.invoice_id) {
                navigate(userPath(`/billing/invoices/${r.invoice_id}`));
                return;
            }
            message.success(t('Saved'));
            onDone();
        } catch (e) {
            message.error(e.error || t('Failed to save'));
        } finally {
            setBusy(false);
        }
    };
    const free = (info?.seats || 0) - (info?.plan?.paid_seats || 0);
    return (
        <Modal open={open} title={t('Change seats')} onCancel={onClose} onOk={submit} confirmLoading={busy} destroyOnHidden>
            <Space direction="vertical">
                <InputNumber min={Math.max(free + 1, info?.users || 0)} value={seats} onChange={(v) => setSeats(v || 0)} style={{ width: 160 }} />
                {quote && quote.delta > 0 && <Text>{t('Due now')}: <b>{formatMoney(quote.due_now, quote.currency)}</b></Text>}
                {quote && <Text type="secondary">{t('Next renewal')}: {formatMoney(quote.renewal, quote.currency)}</Text>}
            </Space>
        </Modal>
    );
};

const CreateTeam = ({ domain, onCreated }) => {
    const { t } = useTranslation();
    const [slug, setSlug] = useState('');
    const [name, setName] = useState('');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        setErr('');
        try {
            await accessApi.createTeam(slug.trim(), name.trim());
            onCreated();
        } catch (e) {
            setErr(e.error || t('Failed to create'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Card>
            <Form layout="vertical" requiredMark={false} style={{ maxWidth: 420 }} onFinish={submit}>
                <Form.Item label={t('Team name')}>
                    <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Acme" />
                </Form.Item>
                <Form.Item label={t('Team address')} validateStatus={err ? 'error' : ''} help={err || undefined}>
                    <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} addonAfter={`.${domain}`} maxLength={32} placeholder="acme" />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={busy} disabled={slug.length < 3}>{t('Get started free')}</Button>
            </Form>
        </Card>
    );
};

const CdnSites = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    const load = useCallback(() => accessApi.cdnSites().then((d) => setRows(d.items)).catch(() => setRows([])), []);
    useEffect(() => { load(); }, [load]);
    const set = async (r, linked, headers) => {
        try {
            await accessApi.cdnLink(r.id, linked, headers);
            load();
        } catch (e) {
            message.error(e.error || t('Failed to save'));
        }
    };
    return (
        <Table size="small" rowKey="id" loading={rows === null} dataSource={rows || []} pagination={{ pageSize: 10, hideOnSinglePage: true }}
            columns={[
                { title: t('Site'), dataIndex: 'domain' },
                { title: t('Receive identity'), render: (_, r) => <Switch checked={r.linked} onChange={(v) => set(r, v, r.headers)} /> },
                { title: t('Extra headers'), render: (_, r) => <Switch size="small" disabled={!r.linked} checked={r.headers} onChange={(v) => set(r, true, v)} /> },
            ]} />
    );
};

/** /access and /verify — one team per account; Access and Verify are free up to 50 users each (Beta). */
const AccessPage = ({ product = 'access' }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [upgrade, setUpgrade] = useState(false);
    const [seats, setSeats] = useState(false);
    const [teamId, setTeamId] = useState(null);
    const title = product === 'verify' ? 'Yeap Verify' : 'Yeap Access';

    const load = useCallback(() => accessApi.overview(teamId).then(setData).catch((e) => setError(e.error || t('Failed to load'))), [t, teamId]);
    useEffect(() => { load(); }, [load]);
    const team = data?.team;
    useEffect(() => {
        if (team?.status !== 'pending') return undefined;
        const id = setInterval(load, 5000);
        return () => clearInterval(id);
    }, [team?.status, load]);

    const teams = data?.teams || [];
    const header = (
        <>
            <PageHeader title={<Space>{title}<Tag color="blue">Beta</Tag></Space>}
                extra={teams.length > 1 ? (
                    <Select value={team?.id} onChange={setTeamId} style={{ minWidth: 200 }}
                        options={teams.map((x) => ({ value: x.id, label: `${x.name} · ${t(ROLE_LABEL[x.role] || x.role)}` }))} />
                ) : null} />
            <Invitations items={data?.invitations} onDone={load} />
        </>
    );
    if (error) return <>{header}<Alert type="error" showIcon message={error} /></>;
    if (!data) return <>{header}<Spin /></>;
    const owner = team?.role === 'owner';
    if (!team) {
        return (
            <>
                {header}
                {data.sales_enabled
                    ? <><Paragraph type="secondary">{t('Free for up to 50 users. One team covers Access and Verify.')}</Paragraph><CreateTeam domain={data.team_domain} onCreated={load} /></>
                    : <Alert type="info" showIcon message={t('Coming soon')} />}
            </>
        );
    }
    if (team.status === 'pending') return <>{header}<Card><Spin tip={t('Setting up your team…')}><div style={{ height: 48 }} /></Spin></Card></>;

    const info = team.products[product];
    if (!owner) {
        // admins and viewers of someone else's team: open the dashboard; billing stays with the owner
        return (
            <>
                {header}
                <Card>
                    <Descriptions column={{ xs: 1, md: 2 }} size="small">
                        <Descriptions.Item label={t('Team')}>{team.name}</Descriptions.Item>
                        <Descriptions.Item label={t('Role')}>{t(ROLE_LABEL[team.role] || team.role)}</Descriptions.Item>
                        <Descriptions.Item label={t('Users')}>{info.users} / {info.seats}</Descriptions.Item>
                        <Descriptions.Item label={t('Server address')}><Text copyable>{team.endpoint}</Text></Descriptions.Item>
                    </Descriptions>
                    <Button type="primary" style={{ marginTop: 16 }} href={team.dashboard_url} target="_blank" rel="noopener" disabled={!team.dashboard_url}>{t('Open dashboard')}</Button>
                </Card>
                {!data.owns_team && data.sales_enabled && (
                    <Collapse ghost style={{ marginTop: 16 }} items={[{ key: 'new', label: t('Create my own team'), children: <CreateTeam domain={data.team_domain} onCreated={load} /> }]} />
                )}
            </>
        );
    }
    const stage = STAGE_TAG[info.stage];
    const plans = data.plans[product];
    const plan = info.plan;
    const full = info.users >= info.seats;

    return (
        <>
            {header}
            {stage && (
                <Alert type={info.stage === 'grace' ? 'warning' : 'error'} showIcon style={{ marginBottom: 16 }}
                    message={t(info.stage === 'grace' ? 'Plan expired. Renew to keep your team connected.' : 'Service is stopped. Renew to restore it.')}
                    description={info.delete_at ? t('Data will be deleted on {{date}} unless you renew.', { date: String(info.delete_at).slice(0, 10) }) : undefined}
                    action={<Button size="small" type="primary" onClick={() => navigate(userPath('/billing/subscription'))}>{t('Renew')}</Button>} />
            )}
            <Card>
                <Descriptions column={{ xs: 1, md: 2 }} size="small">
                    <Descriptions.Item label={t('Team')}>{team.name}</Descriptions.Item>
                    <Descriptions.Item label={t('Plan')}>
                        <Space>{plan ? plan.name : t('Free')}{stage && <Tag color={stage[0]}>{t(stage[1])}</Tag>}</Space>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Users')}>
                        <Text type={full ? 'danger' : undefined}>{info.users} / {info.seats}</Text>
                    </Descriptions.Item>
                    {plan?.expiry_date && <Descriptions.Item label={t('Renews')}>{String(plan.expiry_date).slice(0, 10)}</Descriptions.Item>}
                    {product === 'access'
                        ? <Descriptions.Item label={t('Server address')}><Text copyable>{team.endpoint}</Text></Descriptions.Item>
                        : <Descriptions.Item label={t('Sign-in page')}><Text copyable>{team.verify_host}</Text></Descriptions.Item>}
                </Descriptions>
                <Space wrap style={{ marginTop: 16 }}>
                    <Button type="primary" href={team.dashboard_url} target="_blank" rel="noopener" disabled={!team.dashboard_url}>{t('Open dashboard')}</Button>
                    {plan && plan.status === 'active' && info.stage === 'active'
                        ? <Button onClick={() => setSeats(true)}>{t('Change seats')}</Button>
                        : <Button type={full ? 'primary' : 'default'} onClick={() => setUpgrade(true)}>{t('Upgrade seats')}</Button>}
                    {plan && info.stage !== 'active' && info.users <= info.free_seats && (
                        <Button onClick={() => accessApi.free(product).then(load).catch((e) => message.error(e.error))}>{t('Use free plan')}</Button>
                    )}
                </Space>
                {full && <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>{t('All seats are in use. Upgrade seats to add more users.')}</Paragraph>}
            </Card>
            <Collapse ghost style={{ marginTop: 16 }} items={[
                { key: 'admins', label: t('Team admins'), children: <Members teamId={team.id} /> },
                ...(product === 'access' ? [{ key: 'cdn', label: t('Send user identity to my CDN sites'), children: <CdnSites /> }] : []),
            ]} />
            <UpgradeModal open={upgrade} product={product} plans={plans} info={info} team={team} onClose={() => setUpgrade(false)} />
            <SeatsModal open={seats} product={product} info={info} onClose={() => setSeats(false)} onDone={() => { setSeats(false); load(); }} />
        </>
    );
};

export default AccessPage;
